// Chunk-load resilience for React.lazy boundaries.
//
// Hashed chunk fetches fail for two very different reasons on the mini-site's
// audience (Instagram webviews, hill-station 3G):
//  1. Transient network blips → fixed by a short retry.
//  2. A redeploy invalidated the old hashes while a tab was open → no retry
//     can fix it; only a reload (which picks up the new index.html) can.
// `reloadOnFailure` handles case 2 for page-level chunks, guarded by
// sessionStorage so a genuinely broken deploy can't cause a reload loop.

const RELOADED_KEY = 'tz-chunk-reloaded'

function safeSession(action: 'get' | 'set' | 'remove'): string | null {
  try {
    if (action === 'get') return sessionStorage.getItem(RELOADED_KEY)
    if (action === 'set') sessionStorage.setItem(RELOADED_KEY, '1')
    if (action === 'remove') sessionStorage.removeItem(RELOADED_KEY)
  } catch {
    /* storage blocked (private mode) — degrade to no reload guard */
  }
  return null
}

export function lazyWithRetry<T>(
  importer: () => Promise<T>,
  opts: { reloadOnFailure?: boolean } = {},
): () => Promise<T> {
  return async () => {
    const delays = [300, 1000]
    let lastErr: unknown
    for (let attempt = 0; attempt <= delays.length; attempt++) {
      try {
        const mod = await importer()
        safeSession('remove')
        return mod
      } catch (err) {
        lastErr = err
        if (attempt < delays.length) {
          await new Promise((r) => setTimeout(r, delays[attempt]))
        }
      }
    }
    if (opts.reloadOnFailure && safeSession('get') !== '1') {
      safeSession('set')
      window.location.reload()
      // The page is reloading — park this promise forever so React never
      // renders an error state during the teardown.
      await new Promise<never>(() => {})
    }
    throw lastErr
  }
}
